import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Sets a `<meta>` tag on document.head, creating it if absent.
 * Used as a belt-and-braces complement to react-helmet-async, which has
 * been unreliable at updating OG/Twitter tags on this route in v2.x.
 */
const _setMeta = (selector, attr, value) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const m = selector.match(/\[(\w+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const _setCanonical = (href) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const SEO = ({ 
  title = 'baad.tv - Track TV Shows & Share Reviews with Friends',
  description = 'Free social TV tracker! Track shows across Netflix, Disney+, Prime & more. Rate shows, follow friends & get personalised recommendations. Join the flock today!',
  image = 'https://baad.tv/sheep-logo.png',
  url = 'https://baad.tv',
  type = 'website',
  twitterCard = 'summary_large_image',
  author,
  publishedTime,
  modifiedTime
}) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://baad.tv';
  const fullUrl = url && url.startsWith('http') ? url : `${siteUrl}${url || ''}`;
  const safeImage = image || 'https://baad.tv/sheep-logo.png';
  const fullImageUrl = safeImage.startsWith('http') ? safeImage : `${siteUrl}${safeImage}`;

  // Imperative DOM sync: keep OG/Twitter/canonical meta tags in lockstep
  // with the current page. Necessary because react-helmet-async 2.x doesn't
  // reliably re-render meta tags on client-side route changes for this app.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = title;
    _setMeta('meta[name="description"]', 'content', description);
    _setMeta('meta[property="og:type"]', 'content', type);
    _setMeta('meta[property="og:url"]', 'content', fullUrl);
    _setMeta('meta[property="og:title"]', 'content', title);
    _setMeta('meta[property="og:description"]', 'content', description);
    _setMeta('meta[property="og:image"]', 'content', fullImageUrl);
    _setMeta('meta[name="twitter:card"]', 'content', twitterCard);
    _setMeta('meta[name="twitter:url"]', 'content', fullUrl);
    _setMeta('meta[name="twitter:title"]', 'content', title);
    _setMeta('meta[name="twitter:description"]', 'content', description);
    _setMeta('meta[name="twitter:image"]', 'content', fullImageUrl);
    _setCanonical(fullUrl);
  }, [title, description, fullUrl, fullImageUrl, type, twitterCard]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Baad.tv" />
      <meta property="og:locale" content="en_US" />

      {/* Article specific tags */}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:site" content="@baadtv" />
      <meta name="twitter:creator" content="@baadtv" />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
    </Helmet>
  );
};

export default SEO;
